import { connect, disconnectClient, getClient, getExistingClient, persistSession } from "./tgClient";
import { loadTg } from "./tglib";
import { sessionStore } from "./sessionStore";

export interface LoginHandlers {
  getPhone: () => Promise<string>;
  getCode: () => Promise<string>;
  getPassword: () => Promise<string>;
  onError?: (e: unknown) => void;
}

function errMessage(e: unknown): string {
  const x = e as { errorMessage?: string; message?: string };
  return (x?.errorMessage || x?.message || String(e)).toString();
}

export async function loginInteractive(h: LoginHandlers): Promise<void> {
  const client = await getClient();
  if (!client.connected) await client.connect();
  await client.start({
    phoneNumber: h.getPhone,
    phoneCode: h.getCode,
    password: h.getPassword,
    // Returning false re-prompts (used for a wrong/empty code); returning true
    // stops the flow so fatal errors surface instead of looping forever.
    onError: async (e: unknown) => {
      const msg = errMessage(e);
      h.onError?.(e);
      const retryable = /PHONE_CODE_(INVALID|EMPTY)|PASSWORD_HASH_INVALID/i.test(msg);
      return !retryable;
    },
  });
  await persistSession();
}

export async function resumeIfAuthorized(): Promise<boolean> {
  const apiId = await sessionStore.getApiId();
  const saved = await sessionStore.getSession();
  if (!apiId || !saved) return false;
  const { authorized } = await connect();
  if (authorized) {
    await persistSession(); // re-save in case the DC migrated
    return true;
  }
  // Saved session is no longer valid (e.g. AUTH_KEY_UNREGISTERED): drop it so we
  // don't keep retrying a dead session on every load. The user logs in fresh.
  await sessionStore.clearSession();
  await disconnectClient();
  return false;
}

export interface Me {
  id: string;
  username: string | null;
  firstName: string | null;
  phone: string | null;
}

export async function getMe(): Promise<Me | null> {
  const client = getExistingClient();
  if (!client) return null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const me: any = await client.getMe();
  if (!me) return null;
  return {
    id: me.id?.toString() ?? "",
    username: me.username ?? null,
    firstName: me.firstName ?? null,
    phone: me.phone ?? null,
  };
}

export async function logout(): Promise<void> {
  const client = getExistingClient();
  if (client) {
    try {
      const { Api } = await loadTg();
      await client.invoke(new Api.auth.LogOut());
    } catch {
      /* ignore */
    }
  }
  await disconnectClient();
  await sessionStore.clearAll();
}
